import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CHECKLIST_TYPE_LABEL, DEFECT_STATUS } from "../lib/constants";
import { boatImage } from "../lib/boatImages";
import { Battery, AlertTriangle, ArrowLeft, Calendar as CalIcon, AlertOctagon } from "lucide-react";
import RentwareEmbed from "../components/RentwareEmbed";
import BoatInventory from "../components/BoatInventory";
import BoatLifeVests from "../components/BoatLifeVests";
import { readinessChip } from "./FleetDashboard";

export default function BoatDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [boat, setBoat] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [defects, setDefects] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [knownDefects, setKnownDefects] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignType, setAssignType] = useState("maintenance");
  const [assignTo, setAssignTo] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [battery, setBattery] = useState("");

  // Map role → base path for cross-page navigation (e.g. defect "Åpne" links).
  const basePath =
    user.role === "service" ? "/service" : user.role === "vask" ? "/vask" : "/admin";

  const reload = async () => {
    const b = await api.get(`/boats/${id}`);
    setBoat(b.data);
    setBattery(String(b.data.battery_pct ?? ""));
    const [bk, df, dfk, cl] = await Promise.all([
      api.get("/bookings", { params: { boat_id: id } }),
      api.get("/defects", { params: { boat_id: id } }),
      api.get("/defects", { params: { boat_id: id, status: "known_issue" } }),
      api.get("/checklists", { params: { boat_id: id } }),
    ]);
    setBookings(bk.data);
    setDefects(df.data);
    setKnownDefects(dfk.data);
    setChecklists(cl.data);
    if (user.role === "admin") {
      const u = await api.get("/users");
      setUsers(u.data);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get(`/boats/${id}`).then((b) => {
      if (cancelled) return;
      setBoat(b.data);
      setBattery(String(b.data.battery_pct ?? ""));
      return Promise.all([
        api.get("/bookings", { params: { boat_id: id } }),
        api.get("/defects", { params: { boat_id: id } }),
        api.get("/defects", { params: { boat_id: id, status: "known_issue" } }),
        api.get("/checklists", { params: { boat_id: id } }),
        user.role === "admin" ? api.get("/users") : Promise.resolve({ data: [] }),
      ]).then(([bk, df, dfk, cl, u]) => {
        if (cancelled) return;
        setBookings(bk.data);
        setDefects(df.data);
        setKnownDefects(dfk.data);
        setChecklists(cl.data);
        setUsers(u.data);
      });
    });
    return () => { cancelled = true; };
  }, [id, user.role]);

  if (!boat) return null;

  const onStatusToggle = async () => {
    try {
      await api.patch(`/boats/${id}`, { status: boat.status === "inne" ? "ute" : "inne" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke endre status");
    }
  };

  const onBatterySave = async () => {
    const v = parseInt(battery, 10);
    if (isNaN(v) || v < 0 || v > 100) return toast.error("Batteri må være 0–100");
    await api.patch(`/boats/${id}`, { battery_pct: v });
    toast.success("Batteri oppdatert");
    reload();
  };

  const onAssign = async () => {
    try {
      await api.post("/checklists/assign", {
        boat_id: id,
        type: assignType,
        assigned_to: assignTo || null,
      });
      toast.success("Sjekkliste tildelt");
      setAssignOpen(false);
      reload();
    } catch (e) {
      toast.error("Kunne ikke tildele");
    }
  };

  const assignableUsers = users.filter((u) =>
    assignType === "maintenance" ? ["vask", "service", "admin"].includes(u.role) : ["service", "admin"].includes(u.role)
  );

  return (
    <div className="space-y-6">
      <Link to=".." relative="path" className="inline-flex items-center gap-2 text-sm text-[#2B3F4D] hover:text-[#0C1824]">
        <ArrowLeft className="w-4 h-4" /> Tilbake
      </Link>

      <Card className="overflow-hidden">
        <div className="relative h-56 sm:h-72">
          <img src={boatImage(boat)} alt={boat.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C1824]/85 via-[#0C1824]/40 to-transparent" />
          <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-between text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.32em] text-[#ECC974]">
                  {boat.model} · {boat.length_ft}&apos;
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold mt-1 drop-shadow">{boat.name}</h1>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge
                  className={
                    boat.status === "ute"
                      ? "bg-[#BD984D]/95 text-[#0C1824] hover:bg-[#BD984D]/95 text-sm px-3 py-1"
                      : "bg-[#1DA3B3]/95 text-white hover:bg-[#1DA3B3]/95 text-sm px-3 py-1"
                  }
                  data-testid="boat-detail-status"
                >
                  {boat.status === "ute" ? "Ute" : "Inne"}
                </Badge>
                {(() => {
                  const chip = readinessChip(boat.readiness || "ready");
                  const Icon = chip.icon;
                  return (
                    <Badge
                      className={`${chip.cls} hover:${chip.cls} text-sm px-3 py-1`}
                      data-testid="boat-detail-readiness"
                    >
                      <Icon className="w-3.5 h-3.5 mr-1" /> {chip.label}
                    </Badge>
                  );
                })()}
              </div>
            </div>
            {(user.role === "admin" || user.role === "service") && (
              <div>
                <Button
                  variant="outline"
                  data-testid="boat-toggle-status"
                  onClick={onStatusToggle}
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 hover:text-white"
                >
                  Sett som {boat.status === "inne" ? "Ute" : "Inne"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {boat.readiness === "grounded" && (() => {
        const reds = defects.filter((d) => d.severity === "red");
        const target = reds.length === 1
          ? `${basePath}/defects?focus=${reds[0].id}`
          : `${basePath}/defects?boat=${id}`;
        return (
          <Link to={target} data-testid="boat-grounded-banner-link">
            <Card
              data-testid="boat-grounded-banner"
              className="p-4 bg-red-50 border-l-4 border-l-red-600 border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
            >
              <div className="flex items-start gap-3">
                <AlertOctagon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-900">
                    Båten er sperret (grounded)
                  </div>
                  <p className="text-sm text-red-900/90 mt-0.5">
                    Et eller flere røde avvik må løses eller nedgraderes før denne
                    båten kan settes ute igjen. Forsøk på å sette status «Ute» blir
                    blokkert. <span className="font-semibold underline">Trykk for å åpne saken.</span>
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        );
      })()}
      {boat.readiness === "warning" && (() => {
        const yellows = defects.filter((d) => d.severity === "yellow");
        const target = yellows.length === 1
          ? `${basePath}/defects?focus=${yellows[0].id}`
          : `${basePath}/defects?boat=${id}`;
        return (
          <Link to={target} data-testid="boat-warning-banner-link">
            <Card
              data-testid="boat-warning-banner"
              className="p-4 bg-[#BD984D]/10 border-l-4 border-l-[#BD984D] border-[#BD984D]/30 cursor-pointer hover:bg-[#BD984D]/20 transition-colors"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-[#A57F39] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#0C1824]">
                    Båten kan kjøre med vesentlige begrensninger
                  </div>
                  <p className="text-sm text-[#2B3F4D] mt-0.5">
                    Et eller flere gule avvik bør tas hensyn til før utleie.{" "}
                    <span className="font-semibold underline">Trykk for å åpne saken.</span>
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Åpne avvik
          </div>
          <div className={`text-3xl font-bold mt-2 ${boat.open_defects > 0 ? "text-red-600" : "text-[#0C1824]"}`}>
            {boat.open_defects ?? 0}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Kjente feil
          </div>
          <div className="text-3xl font-bold mt-2 text-slate-700">
            {boat.known_issues ?? 0}
          </div>
          <div className="text-xs text-[#2B3F4D] mt-1">Tolerert · ikke til umiddelbar fiks</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-2">
            <CalIcon className="w-3 h-3" /> Aktiv booking
          </div>
          <div className="text-base font-semibold mt-2 text-[#0C1824]">
            {boat.active_booking ? boat.active_booking.customer_name : "—"}
          </div>
          {boat.active_booking && (
            <div className="text-xs text-[#2B3F4D] mt-1">
              {boat.active_booking.start_date?.slice(0, 10)} →{" "}
              {boat.active_booking.end_date?.slice(0, 10)} · {boat.active_booking.num_guests} pers.
            </div>
          )}
        </Card>
      </div>

      {/* Batteri – status drives av server-side policy (storage = 50 %, ellers 100 %). */}
      {(user.role === "admin" || user.role === "service") && (() => {
        const status = boat.battery_status || "unknown";
        const target = boat.battery_target_pct ?? 100;
        const isStorage = !!boat.storage_mode;
        const isCritical = status === "critical";
        const isOk = status === "ok";
        const daysToNext = boat.days_to_next_booking;
        const policyText = isStorage
          ? `Storage mode – mål ${target} % (45 – 55 % er grønt)`
          : `Aktiv modus – mål ${target} % (≥ 95 % er grønt)${
              daysToNext != null ? ` · ${daysToNext} dag(er) til neste innsjekk` : ""}`;
        return (
          <Card className={`p-4 ${isCritical ? "border-l-4 border-l-red-500" : isOk ? "border-l-4 border-l-emerald-500" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Battery className={`w-5 h-5 ${isCritical ? "text-red-600" : isOk ? "text-emerald-600" : "text-[#A57F39]"}`} />
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-2">
                    Batteri
                    {isStorage && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#1DA3B3]/15 text-[#1DA3B3] text-[10px] font-semibold tracking-wider"
                            data-testid="boat-storage-mode-badge">
                        STORAGE MODE
                      </span>
                    )}
                  </div>
                  <div className={`text-lg font-semibold ${isCritical ? "text-red-600" : isOk ? "text-emerald-700" : "text-[#0C1824]"}`}
                       data-testid="boat-battery-display">
                    {boat.battery_pct ?? "-"} %
                    {isCritical && (
                      <span className="ml-2 text-xs font-normal text-red-600">
                        Avvik fra målnivå ({target} %)
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#2B3F4D] mt-0.5">{policyText}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={battery}
                  onChange={(e) => setBattery(e.target.value)}
                  data-testid="boat-battery-input"
                  className="h-9 w-24"
                />
                <Button onClick={onBatterySave} className="bg-[#1DA3B3] hover:bg-[#2FB5C5] h-9" data-testid="boat-battery-save">
                  Oppdater
                </Button>
              </div>
            </div>
          </Card>
        );
      })()}

      <Tabs defaultValue="checklists">
        <div className="overflow-x-auto -mx-1 pb-1">
          <TabsList className="w-max min-w-full">
            <TabsTrigger value="checklists" data-testid="tab-checklists">Sjekklister</TabsTrigger>
            <TabsTrigger value="defects" data-testid="tab-defects">
              Avvik {defects.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-600 text-white">{defects.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="known" data-testid="tab-known">
              Kjente feil {knownDefects.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-slate-300 text-slate-800">{knownDefects.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookinger</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory">Inventar</TabsTrigger>
            <TabsTrigger value="lifevests" data-testid="tab-lifevests">Flytevester</TabsTrigger>
            <TabsTrigger value="rentware" data-testid="tab-rentware">Rentware</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="checklists" className="mt-5 space-y-3">
          {user.role === "admin" && (
            <div className="flex justify-end">
              <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="assign-checklist-open" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                    + Tildel sjekkliste
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Tildel ny sjekkliste</DialogTitle>
                    <DialogDescription>
                      Opprett en sjekkliste på denne båten. Velg type og om den skal være åpen for
                      første ledige eller tildelt en spesifikk person.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Type</Label>
                      <Select value={assignType} onValueChange={setAssignType}>
                        <SelectTrigger data-testid="assign-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="maintenance">Vedlikehold/Renhold (Vask/Service)</SelectItem>
                          <SelectItem value="checkin">Innsjekk (Service)</SelectItem>
                          <SelectItem value="checkout">Utsjekk (Service)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tildel til</Label>
                      <Select
                        value={assignTo || "__open__"}
                        onValueChange={(v) => setAssignTo(v === "__open__" ? "" : v)}
                      >
                        <SelectTrigger data-testid="assign-user"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__open__">
                            Åpen pulje – først til mølla
                          </SelectItem>
                          {assignableUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} ({u.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-[#2B3F4D] mt-1.5">
                        Lar du den stå på «Åpen pulje» får alle med riktig rolle se den i sin
                        liste, og den første som tar den får ansvar for å fullføre.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={onAssign} data-testid="assign-checklist-submit" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                      Tildel
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {checklists.length === 0 && (
            <Card className="p-6 text-sm text-[#2B3F4D]">Ingen sjekklister for denne båten.</Card>
          )}
          {checklists.map((cl) => (
            <Card key={cl.id} className="p-4 flex items-center justify-between" data-testid={`cl-row-${cl.id}`}>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
                  {CHECKLIST_TYPE_LABEL[cl.type]}
                </div>
                <div className="font-semibold text-[#0C1824]">
                  {cl.status === "completed" ? "Fullført" : cl.status === "pending" ? "Venter" : "Pågår"}
                  {cl.performed_by_name && ` · av ${cl.performed_by_name}`}
                </div>
                <div className="text-xs text-[#2B3F4D] mt-1">
                  {cl.completed_at
                    ? `Fullført ${new Date(cl.completed_at).toLocaleString("nb-NO")}`
                    : `Opprettet ${new Date(cl.created_at).toLocaleString("nb-NO")}`}
                </div>
              </div>
              <Link to={`/checklist/${cl.id}`}>
                <Button variant="outline" className="border-[#0C1824] text-[#0C1824]">
                  {cl.status === "completed" ? "Se" : "Fullfør"}
                </Button>
              </Link>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="defects" className="mt-5 space-y-3">
          {defects.length === 0 && <Card className="p-6 text-sm text-[#2B3F4D]">Ingen avvik registrert.</Card>}
          {defects.map((d) => (
            <Card key={d.id} className="p-4" data-testid={`defect-row-${d.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <Badge
                    className={
                      d.status === "resolved"
                        ? "bg-[#1DA3B3]/15 text-[#1DA3B3]"
                        : d.status === "in_progress"
                        ? "bg-[#D4B060]/20 text-[#A57F39]"
                        : "bg-red-100 text-red-700"
                    }
                  >
                    {DEFECT_STATUS[d.status]}
                  </Badge>
                  <div className="font-semibold text-[#0C1824] mt-1">{d.title}</div>
                  <div className="text-xs text-[#2B3F4D] mt-1">
                    {new Date(d.created_at).toLocaleString("nb-NO")} · kilde: {d.source}
                  </div>
                </div>
                <Link to={`${basePath}/defects?focus=${d.id}`}>
                  <Button variant="outline" className="border-[#0C1824] text-[#0C1824]" data-testid={`defect-open-${d.id}`}>Åpne</Button>
                </Link>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="known" className="mt-5 space-y-3">
          {knownDefects.length === 0 && (
            <Card className="p-6 text-sm text-[#2B3F4D]">
              Ingen kjente feil registrert på denne båten.
            </Card>
          )}
          {knownDefects.map((d) => (
            <Card key={d.id} className="p-4 border-l-4 border-l-slate-400" data-testid={`known-row-${d.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <Badge className="bg-slate-200 text-slate-700">Kjent feil på båt</Badge>
                  <div className="font-semibold text-[#0C1824] mt-1">{d.title}</div>
                  {d.description && <div className="text-sm text-[#2B3F4D] mt-1">{d.description}</div>}
                  <div className="text-xs text-[#2B3F4D] mt-2">
                    Registrert {new Date(d.created_at).toLocaleString("nb-NO")} · kilde: {d.source}
                  </div>
                </div>
                <Link to={`${basePath}/defects?focus=${d.id}`}>
                  <Button variant="outline" className="border-[#0C1824] text-[#0C1824]" data-testid={`known-open-${d.id}`}>Åpne</Button>
                </Link>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="bookings" className="mt-5 space-y-3">
          {bookings.length === 0 && <Card className="p-6 text-sm text-[#2B3F4D]">Ingen bookinger registrert.</Card>}
          {[...bookings].sort((a, b) => {
            // Sortér etter nærhet til i dag: aktive først, deretter kommende
            // (nærmeste øverst), til slutt fullførte som ikke er arkivert
            // (typisk pga. uløst skadesak) – nyeste først.
            const today = new Date().toISOString().slice(0, 10);
            const bucket = (bk) => {
              const s = (bk.start_date || "").slice(0, 10);
              const e = (bk.end_date || "").slice(0, 10);
              if (s && e && s <= today && today <= e) return 0; // aktiv
              if (s && s > today) return 1; // kommende
              return 2; // fullført-med-hendelse
            };
            const ba = bucket(a);
            const bb = bucket(b);
            if (ba !== bb) return ba - bb;
            const sa = (a.start_date || "").slice(0, 10);
            const sb = (b.start_date || "").slice(0, 10);
            if (ba === 2) return sb.localeCompare(sa); // fullført – nyeste øverst
            return sa.localeCompare(sb); // aktiv/kommende – nærmest øverst
          }).map((b) => (
            <Card key={b.id} className="p-4" data-testid={`booking-row-${b.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-[#0C1824]">{b.customer_name}</div>
                  <div className="text-xs text-[#2B3F4D] mt-1">
                    {b.start_date?.slice(0, 10)} → {b.end_date?.slice(0, 10)} · {b.num_guests} pers.
                  </div>
                </div>
                <Badge className="bg-[#BD984D]/15 text-[#A57F39]">{b.source || "manual"}</Badge>
              </div>
              {b.notes && <div className="mt-2 text-sm text-[#2B3F4D]">{b.notes}</div>}
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="rentware" className="mt-5">
          <RentwareEmbed
            html={boat.rentware_embed_html}
            fallback="Lim inn widget-snippet under Innstillinger → Rentware for å vise live kalender her."
          />
        </TabsContent>

        <TabsContent value="inventory" className="mt-5">
          <BoatInventory boatId={id} />
        </TabsContent>

        <TabsContent value="lifevests" className="mt-5">
          <BoatLifeVests boatId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
